import Knowledge, {
  KnowledgeLanguage,
  KnowledgeMode,
  WebscrapFrequency,
} from './Knowledge';
import { ResourceAccess } from './ResourceAccess';

export enum AgentCapability {
  TRANSFER_CALLS = 'transfer_calls',
  INSERT_APPOINTMENT = 'insert_appointment',
}

export enum AgentStatus {
  IN_REVIEW = 'review', // For now, this matches the value stored in the database.
  ONLINE = 'online',
  OFFLINE = 'offline',
}

export enum AgentMetricsUnit {
  DEFAULT = 'default',
  CURRENCY = 'currency',
  PERCENT = 'percent',
  MINUTE = 'minute',
}

export enum AgentLanguage {
  ENGLISH = 'english',
  FRENCH = 'french',
  GERMAN = 'german',
}

export enum AgentType {
  CALL = 'call',
  CHAT = 'chat',
}

export type AnalyticsCategory = {
  name: string;
  description: string;
  email?: string;
};

export type AnalyticsFieldKind = 'text' | 'yes_no' | 'number' | 'choice';

// What the post-call analytics pulls out of each call. Not the same as
// data_to_collect, which the agent asks for during the call.
export type AnalyticsField = {
  name: string;
  description: string;
  kind: AnalyticsFieldKind;
  range?: [number, number];
  options?: string[];
};

// One analytics field as the form edits it. The range is kept as text
// while typing and turned into numbers on save.
export type AnalyticsFieldFormRow = {
  name: string;
  description: string;
  kind: AnalyticsFieldKind;
  options: string[];
  range_low: string;
  range_high: string;
};

export type WorkingHours = {
  days: number[]; // 0=Mon..6=Sun
  start: string; // "HH:MM"
  end: string; // "HH:MM"
};

export interface Agent {
  avatar?: string;
  company: string;
  company_id?: string;
  custom_phone_number?: string;
  default_language?: AgentLanguage;
  default_sentence: string;
  data_to_collect?: string[];
  external_capabilities?: {
    transfer_calls?: DepartmentContact[] | 'employee_directory';
    insert_appointment?: Appointment[];
  };
  id?: string;
  knowledge_ids: string[];
  name: string;
  role_id: string;
  opening_sentence: string;
  status: AgentStatus;
  system_phone_number?: string;
  system_phone_number_country_code?: string;
  /** MixVoIP / SIP inbound target. Empty when the agent is PSTN-only. */
  sip_address?: string;
  sip_username?: string;
  sip_domain?: string;
  routing_provider?: string;
  /** Partner PBX route/extension id (cockpit metadata). */
  routing_external_id?: string;
  user_id?: string;
  voice: AgentVoice;
  call_categories?: AnalyticsCategory[] | null;
  analytics_language?: string;
  analytics_fields?: AnalyticsField[];
  summary_instruction?: string;
  access?: ResourceAccess;
  // `| null` on working_hours/feedback_sampling_rate is a deliberate wire
  // signal: the update payload sends `null` when the user has explicitly
  // cleared the field, since an omitted/undefined key is dropped by
  // JSON.stringify and would leave the previously saved value untouched.
  working_hours?: WorkingHours | null;
  time_zone?: string;
  holidays?: string[]; // "MM-DD" (recurring) or "YYYY-MM-DD" (one-off)
  auto_fix?: boolean;
  feedback_sampling_rate?: number | null; // 0.0-1.0
  failure_backup_phone_number?: string;
}

export interface AgentWithAttachedKnowledge extends Agent {
  attachedKnowledge?: Knowledge;
}

export interface AgentFormData {
  avatar: string;
  agent_name: string;
  insert_appointment: Appointment[];
  company: string;
  custom_phone_number: string;
  default_language: AgentLanguage;
  default_sentence: string;
  data_to_collect: string[];
  external_capabilities: string | string[];
  knowledge_ids: string[];
  knowledge_name: string;
  knowledge_language: KnowledgeLanguage;
  existing_knowledge_id: string;
  knowledge_mode?: KnowledgeMode;
  webscrap_enabled?: boolean;
  webscrap_frequency?: WebscrapFrequency;
  webscrap_source_url?: string;
  webscrap_use_sitemap?: boolean;
  webscrap_max_depth?: number;
  webscrap_max_pages?: number;
  webscrap_pages_per_pdf?: number;
  webscrap_links?: { url: string; depth?: number }[];
  webscrap_exclude_url_patterns?: { value: string }[];
  webscrap_scrape_images?: boolean;
  webscrap_digests?: {
    heading: string;
    url: string;
    item_selector: string;
    title_selector: string;
    date_attr: string;
    drop_past: boolean;
    max_items?: number;
    page_url_template?: string;
    fetch_detail_pages: boolean;
    detail_link_selector?: string;
    max_pages?: number;
  }[];
  opening_sentence: string;
  role_id: string;
  status: AgentStatus;
  system_phone_number: string;
  system_phone_number_country_code: string;
  transfer_calls: DepartmentContact[] | 'employee_directory';
  // Staged employee directory file during agent creation, uploaded once a
  // real agent id exists (see EmployeeDirectoryUploadModal's deferUpload mode).
  employee_directory_staged_file?: { fileName: string; base64: string } | null;
  voice: AgentVoice;
  // Analytics
  analytics_language: string;
  call_categories: AnalyticsCategory[];
  unknown_category_email: string;
  analytics_fields: AnalyticsFieldFormRow[];
  summary_instruction: string;
  access: ResourceAccess;
  // Scheduling, failover & autofix
  working_hours?: WorkingHours;
  time_zone: string;
  holidays: string[];
  auto_fix: boolean;
  feedback_sampling_rate?: number;
  failure_backup_phone_number: string;
}

export type AgentRole = {
  id: string;
  name: string;
};

export type AgentVoice = 'male' | 'female';

export type Appointment = {
  start_time: string;
  end_time: string;
  min_advance_notice_hours: string;
  appointment_length_minutes: string;
  app: 'outlook' | 'google';
};

export type DepartmentContact = {
  department_name: string;
  phone_number: string;
  email?: string;
};
